// src/pages/wizard/Step3AccessScope.jsx
//
// Connector-aware scope/workspace selection.
// - GitHub: lets the customer pick which org or personal account to sync from
// - Other connectors: shows a confirmation card (workspace is determined by OAuth)

import React, { useState, useEffect, useCallback } from 'react';
import { Check, Building2, User, Loader2, AlertCircle } from 'lucide-react';
import { apiCall } from '../../utils/api';

export default function Step3AccessScope({ connector, configId, scope, onChange }) {
  const connectorKey = (connector?.id || connector?.key || '').toLowerCase();

  // GitHub-specific: org picker
  if (connectorKey === 'github') {
    return (
      <GithubOrgPicker
        configId={configId}
        scope={scope}
        onChange={onChange}
      />
    );
  }

  // Default: workspace is locked from OAuth, show confirmation
  return (
    <DefaultScopeConfirmation connector={connector} />
  );
}

// ---- GitHub org picker ----
function GithubOrgPicker({ configId, scope, onChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadOrgs = useCallback(async () => {
    if (!configId) {
      setError('Missing connector config. Please go back and complete authentication.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall(`/oauth/github/orgs?config_id=${configId}`);
      if (!res.ok) {
        throw new Error(`Failed to load organizations (HTTP ${res.status})`);
      }
      const data = await res.json();
      setOrgs(data.orgs || []);
      // Pre-select the currently-configured org if any, else the first option
      const currentLogin = (data.current || '').trim();
      const initial =
        (data.orgs || []).find((o) => o.login === currentLogin) ||
        (data.orgs || [])[0] ||
        null;
      setSelected(initial);
      if (initial && onChange) {
        onChange([{ id: initial.login, name: initial.name, type: initial.type }]);
      }
    } catch (err) {
      console.error('Failed to load GitHub orgs:', err);
      setError(err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
    // Intentionally exclude onChange from deps to avoid re-fetching on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);


  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  async function handleSelect(org) {
    setSelected(org);
    setSaving(true);
    try {
      // Persist the org choice to config_json
      const res = await apiCall(`/sync/configs/${configId}`, {
        method: 'PATCH',
        body: JSON.stringify({ org: org.login }),
      });
      if (!res.ok) {
        throw new Error(`Failed to save selection (HTTP ${res.status})`);
      }
      if (onChange) {
        onChange([{ id: org.login, name: org.name, type: org.type }]);
      }
    } catch (err) {
      console.error('Failed to save org selection:', err);
      setError(err.message || 'Failed to save selection');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Header
          title="Choose GitHub Account"
          subtitle="Pick which account or organization to sync from."
        />
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Header
          title="Choose GitHub Account"
          subtitle="Pick which account or organization to sync from."
        />
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-400 font-medium mb-1">
              Could not load organizations
            </p>
            <p className="text-xs text-[#a0a0a0]">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header
        title="Choose GitHub Account"
        subtitle="Pick which account or organization to sync from. You can create separate connectors for additional accounts later."
      />

      <div className="space-y-3">
        {orgs.map((org) => {
          const isSelected = selected?.login === org.login;
          return (
            <button
              key={org.login}
              onClick={() => handleSelect(org)}
              disabled={saving}
              className={`
                w-full p-4 rounded-lg border-2 text-left transition-all
                ${isSelected
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-[#2a2a2a] hover:border-[#3a3a3a] bg-[#1a1a1a]'
                }
                ${saving ? 'opacity-60 cursor-wait' : ''}
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                    ${isSelected
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-[#3a3a3a]'
                    }
                  `}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  {org.avatar_url ? (
                    <img
                      src={org.avatar_url}
                      alt=""
                      className="w-9 h-9 rounded-md flex-shrink-0"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-md bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
                      {org.type === 'personal' ? (
                        <User className="w-4 h-4 text-[#a0a0a0]" />
                      ) : (
                        <Building2 className="w-4 h-4 text-[#a0a0a0]" />
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${isSelected ? 'text-blue-400' : 'text-white'}`}>
                      {org.name || org.login}
                    </p>
                    <p className="text-xs text-[#6a6a6a]">
                      {org.type === 'personal' ? 'Personal account' : 'Organization'} · @{org.login}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {orgs.length === 1 && orgs[0].type === 'personal' && (
        <div className="p-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg">
          <p className="text-xs text-[#a0a0a0]">
            Only your personal GitHub account is available. To sync from a company organization,
            ensure you have member access to it on GitHub and reconnect.
          </p>
        </div>
      )}
    </div>
  );
}

// ---- Default scope confirmation (for non-GitHub connectors) ----
function DefaultScopeConfirmation({ connector }) {
  return (
    <div className="space-y-6">
      <Header
        title="Access Confirmed"
        subtitle={`The ${connector?.name || 'connector'} workspace is set automatically based on your authentication. Click Next to proceed.`}
      />
      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-start gap-3">
        <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-400 font-medium mb-1">
            Workspace ready
          </p>
          <p className="text-xs text-[#a0a0a0]">
            Connex will sync from the workspace you authenticated with. You can connect additional
            workspaces by creating new connectors.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---- Shared header ----
function Header({ title, subtitle }) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-white mb-2">{title}</h2>
      <p className="text-sm text-[#a0a0a0]">{subtitle}</p>
    </div>
  );
}
